import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { Check, ShieldCheck, Info } from "lucide-react";

const PERKS = [
  "Dauerhafter Zugriff auf alle konkreten Ernährungsempfehlungen",
  "Umfangreiche Rezept-Datenbank für knochengesunde Mahlzeiten",
  "Wochenplan & Nährstoff-Leitfäden zum Nachkochen",
  "Probiotika-Darm-Kur – Schritt für Schritt",
  "Zukünftige Rezept-Updates inklusive",
];

export default function Paywall() {
  const { refreshUser } = useAuth();
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ configured: false }));
  }, []);

  const createOrder = async () => {
    const { data } = await api.post("/paypal/create-order");
    return data.id;
  };

  const onApprove = async (data) => {
    try {
      await api.post("/paypal/capture-order", { order_id: data.orderID });
      await refreshUser();
      toast.success("Zahlung erfolgreich – dein Premium-Zugang ist freigeschaltet!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Zahlung fehlgeschlagen.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="paywall-container">
      <div className="grid lg:grid-cols-2 gap-8 items-stretch">
        <div className="bg-[#1E3F20] text-white rounded-3xl p-8 sm:p-10 fb-grain">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#D99B26]">Premium-Zugang</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold leading-tight">
            Konkrete Rezepte &amp; Ernährungspläne
          </h1>
          <div className="mt-6 flex items-baseline gap-2">
            <span className="font-display text-5xl font-bold text-white">19,99 €</span>
            <span className="text-[#C7D5C4]">einmalig · kein Abo</span>
          </div>
          <ul className="mt-8 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[#E2EBE1]">
                <Check className="w-5 h-5 text-[#D99B26] shrink-0 mt-0.5" /> <span className="text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 flex items-center gap-2 text-xs text-[#8FA58C]">
            <ShieldCheck className="w-4 h-4" /> Nach erfolgreicher Zahlung dauerhafter Zugang zu deinem Konto.
          </p>
        </div>

        <div className="bg-white rounded-3xl ring-1 ring-[#E2DDD3] shadow-xl p-8 sm:p-10 flex flex-col justify-center" data-testid="paypal-checkout-modal">
          <h2 className="font-display text-2xl font-bold text-[#1C2D24] mb-2">Jetzt freischalten</h2>
          <p className="text-[#4A5D52] text-sm mb-6">Sichere Einmalzahlung über PayPal.</p>

          {!config && <div className="h-24 rounded-xl bg-[#F3EFE6] animate-pulse" />}

          {config && config.configured && (
            <div data-testid="paypal-pay-button">
              <PayPalScriptProvider options={{ clientId: config.client_id, currency: config.currency || "EUR", intent: "capture" }}>
                <PayPalButtons style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }} createOrder={createOrder} onApprove={onApprove} onError={() => toast.error("PayPal-Fehler. Bitte erneut versuchen.")} />
              </PayPalScriptProvider>
            </div>
          )}

          {config && !config.configured && (
            <div className="rounded-2xl bg-[#FCF3E7] border border-[#E8D3B5] p-5" data-testid="paypal-not-configured">
              <p className="flex items-start gap-2 text-sm text-[#8a5a2b] leading-relaxed">
                <Info className="w-5 h-5 shrink-0 mt-0.5" />
                <span>PayPal ist noch nicht verbunden. Sobald die PayPal-Zugangsdaten hinterlegt sind, erscheint hier der Bezahl-Button und die Zahlung ist sofort möglich.</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
